import { useEffect, useRef } from "react";
import {
  WatchQueryFetchPolicy,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { OC_PROPERTY_SUBSCRIPTION } from "oc/graphql";
import { OC_GET_PROPERTY } from "oc/graphql";

export const useOcTwoFactorEnabled = ({
  onCompleted,
  onError,
  fetchPolicy = "cache-and-network",
  pollInterval = 0,
}: {
  onCompleted?: (data: { key: string; value: any; validFrom?: string }) => void;
  onError?: (error: { key: string; value: any }) => void;
  fetchPolicy?: WatchQueryFetchPolicy;
  pollInterval?: number;
}) => {
  const resource = "system";
  const key = "2faEnabled";
  const subscription = true;
  const onCompletedCalledRef = useRef(false);
  const onErrorCalledRef = useRef(false);

  const { data, loading, error, refetch } = useQuery(OC_GET_PROPERTY, {
    fetchPolicy: fetchPolicy,
    pollInterval: pollInterval,
    variables: {
      resource,
      key,
    },
  });

  // Handle onCompleted callback via useEffect
  useEffect(() => {
    if (data && !loading && onCompleted && !onCompletedCalledRef.current) {
      onCompletedCalledRef.current = true;
      onCompleted({
        key,
        value: data?.property?.value,
        validFrom: data?.property?.validFrom,
      });
    }
  }, [data, loading, onCompleted]);

  // Handle onError callback via useEffect
  useEffect(() => {
    if (error && onError && !onErrorCalledRef.current) {
      onErrorCalledRef.current = true;
      onError({ key, value: error });
    }
  }, [error, onError]);

  useSubscription(OC_PROPERTY_SUBSCRIPTION, {
    skip: !subscription || !resource || !key,
    variables: {
      resource,
      key,
    },
    onData: () => {
      onCompletedCalledRef.current = false; // Allow callback again on subscription update
      refetch();
    },
  });

  return {
    data: data?.property?.value,
    validFrom: data?.property?.validFrom,
    loading,
    error,
    refetch,
    onCompleted,
    onError,
  };
};

export default useOcTwoFactorEnabled;
